import { useEffect, useMemo, useState } from "react";

import { GrowthUnifiedProfile } from "@/libraries/growth-unified-profile-client";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import { useUserContext } from "../../../../components/user-context";
import { useLoggedInUser } from "../../../../utils";
import { useGeoLocation } from "../../../../utils/useGeoCurrency";
import { useWAEContext } from "../../../utils/wae-context-provider";

import { getSoftwarePageProductRecommendation } from "./software-page-product-recommendation";

export enum UserSegmentTypes {
    LOGIN_STATUS = "login-status",
    GEOLOCATION = "geolocation",
    JOB_FUNCTION = "job-function",
    DOMAIN_TYPE = "domain-type",
    OWNED_PRODUCTS = "owned-products",
    CLUSTER_TAG = "cluster-tag",
    FIRST_RECOMMENDATION = "first-recommendation",
    SECOND_RECOMMENDATION = "second-recommendation",
    THIRD_RECOMMENDATION = "third-recommendation",
    FOURTH_RECOMMENDATION = "fourth-recommendation"
}

export interface UserSegmentResponse {
    data?: UserSegmentValues;
    error?: unknown;
    segmentFetchErrors?: UserSegmentTypes[];
    status: "idle" | "fetching" | "complete";
}

export type ProductRecommendationOptions =
    | "jira"
    | "confluence"
    | "jsm"
    | "loom"
    | "jpd"
    | "rovo";
export interface UserSegmentValues {
    [UserSegmentTypes.LOGIN_STATUS]?: "loggedIn" | "loggedOut";
    [UserSegmentTypes.GEOLOCATION]?: string;
    [UserSegmentTypes.JOB_FUNCTION]?: string;
    [UserSegmentTypes.DOMAIN_TYPE]?: string;
    [UserSegmentTypes.OWNED_PRODUCTS]?: string[];
    [UserSegmentTypes.CLUSTER_TAG]?: string;
    [UserSegmentTypes.FIRST_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.SECOND_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.THIRD_RECOMMENDATION]?: ProductRecommendationOptions;
    [UserSegmentTypes.FOURTH_RECOMMENDATION]?: ProductRecommendationOptions;
}

// temporary implementation for testing purposes
export const useUserSegments = (): UserSegmentResponse => {
    const [status, setStatus] = useState<"idle" | "fetching" | "complete">(
        "idle"
    );
    const [profile, setProfile] = useState<Partial<GrowthUnifiedProfile>>();
    const [profileError, setProfileError] = useState<unknown>(null);
    const [segmentFetchErrors, setSegmentFetchErrors] = useState<
        UserSegmentTypes[]
    >([]);

    const loggedInUser = useLoggedInUser();
    const { personalizationValues } = useWAEContext();
    const { loggedOutProfile, loggedInProfile } = useUserContext();

    // Memoize the client to prevent recreation on every render
    const { getFullProfileCallback } = useGupsStatus();
    const envSource = typeof window !== "undefined" ? window : process.env;
    const { countryCode, status: locationStatus } = useGeoLocation(
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string
    );
    useEffect(() => {
        if (loggedInProfile) {
            if (loggedInUser.isUserLoggedIn) {
                setProfile(loggedInProfile);
            }
            setSegmentFetchErrors([]);
            setStatus("complete");
            return;
        }

        let isCancelled = false;
        const getFullProfile = async () => {
            setStatus("fetching");
            const { data, error } = await getFullProfileCallback();
            // Add this to prevent Race Condition when the component is unmounted before the request is complete
            if (isCancelled) return;
            setProfileError(error);
            setProfile(data);
            setSegmentFetchErrors(
                error
                    ? [
                          UserSegmentTypes.JOB_FUNCTION,
                          UserSegmentTypes.DOMAIN_TYPE
                      ]
                    : []
            );
            setStatus("complete");
        };

        getFullProfile();

        return () => {
            isCancelled = true;
        };
    }, [getFullProfileCallback, loggedInUser.isUserLoggedIn, loggedInProfile]);

    // Helper function to extract product names from sites
    const extractProductNames = (
        sites?: Array<{
            products?: Array<{ productName?: string; productKey?: string }>;
        }>
    ) => {
        if (!sites) return [];

        return sites.flatMap(
            (site) =>
                site.products
                    ?.map(
                        (product) =>
                            product.productName || product.productKey || ""
                    )
                    .filter(Boolean) || []
        );
    };

    // Memoize the personalization values to prevent unnecessary recalculations
    return useMemo(() => {
        // Collect products from both logged-in and logged-out profiles
        const loggedInProducts = extractProductNames(profile?.sites);
        const loggedOutProducts = extractProductNames(loggedOutProfile?.sites);
        const allProducts = [...loggedInProducts, ...loggedOutProducts];
        const uniqueProducts = [...new Set(allProducts)];
        const productRecommendation = getSoftwarePageProductRecommendation({
            jobFunction: profile?.userProfile?.jobFunction,
            loggedInProducts: loggedInProducts
        });

        // Build the user segment values
        const userSegmentData: UserSegmentValues = {
            ...personalizationValues,
            [UserSegmentTypes.LOGIN_STATUS]: loggedInUser.isUserLoggedIn
                ? "loggedIn"
                : "loggedOut",
            [UserSegmentTypes.GEOLOCATION]:
                locationStatus === "complete" ? countryCode : undefined,
            [UserSegmentTypes.JOB_FUNCTION]: profile?.userProfile?.jobFunction,
            [UserSegmentTypes.DOMAIN_TYPE]: profile?.userProfile?.domainType,
            [UserSegmentTypes.OWNED_PRODUCTS]: uniqueProducts,
            [UserSegmentTypes.FIRST_RECOMMENDATION]:
                productRecommendation.FIRST_Recommendation as ProductRecommendationOptions,
            [UserSegmentTypes.SECOND_RECOMMENDATION]:
                productRecommendation.SECOND_Recommendation as ProductRecommendationOptions,
            [UserSegmentTypes.THIRD_RECOMMENDATION]:
                productRecommendation.THIRD_Recommendation as ProductRecommendationOptions,
            [UserSegmentTypes.FOURTH_RECOMMENDATION]:
                productRecommendation.FOURTH_Recommendation as ProductRecommendationOptions
        };

        return {
            data: userSegmentData,
            error: profileError,
            segmentFetchErrors,
            status
        };
    }, [
        loggedInUser.isUserLoggedIn,
        profile?.sites,
        profile?.userProfile?.domainType,
        profile?.userProfile?.jobFunction,
        loggedOutProfile?.sites,
        profileError,
        segmentFetchErrors,
        countryCode,
        status,
        locationStatus,
        personalizationValues
    ]);
};
